/**
 * TRACI-916: an "ok" typed at the AI's connect offer is a yes to that offer.
 *
 * The offer card (MessageRenderer's SoftHandoffCard, escalation "offer") counts down only while
 * it is the latest message. A shopper who answered "Connecting you to Sales now" with "ok" or
 * "thanks" stopped the countdown by typing, the AI got "ok" as a new turn, and it offered Sales
 * again: "Connecting you to Sales now", over and over (prod, 22 and 23 Oct 2026). The send path
 * asks this first and, for a bare acknowledgement, connects through the card instead of
 * sending the words to the AI.
 */

import type { Message } from '../types/chat.types';

// Every word of the message must be one of these, so "no thanks", "ok but what about price"
// and "thanks, what size?" are not a yes. "fine" stays out: "I'm fine" is a no.
const YES_WORDS = new Set([
  'ok', 'okay', 'okey', 'okie', 'k', 'kk', 'o.k', 'sure', 'yes', 'yeah', 'yep', 'yup', 'ya',
  'please', 'go', 'ahead', 'sounds', 'good',
]);
// Thanks-type words count only while the card is still counting down ("Connecting you..." ->
// "thanks"). On the paused card ("Still want a Tire Expert?") a "thanks" or "got it" can be a
// goodbye, and connecting someone unasked is worse than one more AI turn.
const THANKS_WORDS = new Set([
  'thanks', 'thank', 'you', 'thx', 'ty', 'great', 'perfect', 'cool', 'awesome', 'alright',
  'all', 'right', 'got', 'it', 'sweet',
]);
const MAX_ACK_WORDS = 5;
/** How long the card counts down (MessageRenderer's SOFT_DURATION_MS). The latest card older
 *  than this has paused: had it been counting, it would have connected already. */
export const OFFER_COUNTDOWN_MS = 10_000;

/** True when the whole message is a short acknowledgement ("ok", "ok thanks", "yes please", "👍").
 *  `paused`: the card has stopped counting, so only a yes counts, not a thank-you. */
export function isOfferAcknowledgement(text: string | null | undefined, paused = false): boolean {
  const t = (text || '').trim().toLowerCase();
  if (!t) return false;
  if (/^(?:\u{1F44D}[\u{1F3FB}-\u{1F3FF}]?\s*)+$/u.test(t)) return true;   // thumbs up, any skin tone
  const words = t.replace(/[!.,?~\u{1F44D}\u{1F3FB}-\u{1F3FF}]+/gu, ' ').split(/\s+/).filter(Boolean);
  const ok = (w: string) => YES_WORDS.has(w) || (!paused && THANKS_WORDS.has(w));
  return words.length > 0 && words.length <= MAX_ACK_WORDS && words.every(ok)
    && (!paused || words.some((w) => YES_WORDS.has(w)));
}

/** The department to connect when a typed `text` answers the offer card, else null. Only when
 *  the card is the latest message the shopper can see; a card with anything after it is not
 *  what they are answering. */
export function offerAckDepartment(latest: Message | undefined, text: string | null | undefined,
                                   now: number = Date.now()): string | null {
  const card = latest?.parts?.find((p) => p.type === 'soft_handoff');
  if (!card) return null;
  const paused = now - (card.offeredAt ?? 0) > OFFER_COUNTDOWN_MS;
  return isOfferAcknowledgement(text, paused) ? (card.department || 'Customer Care') : null;
}
